/**
 * HEADER section: $ACADVER (parametric), $HANDSEED, $INSUNITS,
 * $PDMODE/$PDSIZE (system vars) and $EXTMIN/$EXTMAX fitted to the exported
 * geometry. Version gates live in types.ts; this file only reports.
 */

import type { Entity } from '../../../../state/store';
import { useSystemVarsStore } from '../../../../state/useSystemVarsStore';
import { entityToPolyline } from '../../../../core/commands/runners/lib';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';

/** Absorb every geometry carrier's bounds (polylines, texts, tables, dims). */
export function drawingBounds(entities: Entity[]): { min: number[]; max: number[] } {
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    const absorb = (x: number, y: number, z: number): void => {
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        minX = Math.min(minX, x); minY = Math.min(minY, y); minZ = Math.min(minZ, z ?? 0);
        maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); maxZ = Math.max(maxZ, z ?? 0);
    };
    entities.forEach((e) => {
        const poly = entityToPolyline(e);
        if (poly && poly.length >= 2) poly.forEach((p) => absorb(p[0], p[1], p[2] ?? 0));
        const d = e.data as Record<string, any>;
        (['position', 'textPosition'] as const).forEach((k) => {
            if (Array.isArray(d[k])) absorb(d[k][0], d[k][1], d[k][2] ?? 0);
        });
        (['point1', 'point2'] as const).forEach((k) => {
            if (Array.isArray(d[k])) absorb(d[k][0], d[k][1], d[k][2] ?? 0);
        });
        if (e.type === 'table' && Array.isArray(d.cells)) {
            const pos = d.position ?? [0, 0, 0];
            const cols = d.cells.reduce((m: number, r: string[]) => Math.max(m, r?.length ?? 0), 0);
            absorb((pos[0] ?? 0) + cols * ((d.colWidth ?? 2) as number), (pos[1] ?? 0) - d.cells.length * ((d.rowHeight ?? 0.6) as number), pos[2] ?? 0);
        }
    });
    if (!Number.isFinite(minX)) return { min: [0, 0, 0], max: [0, 0, 0] };
    return { min: [minX, minY, minZ], max: [maxX, maxY, maxZ] };
}

export function writeHeader(w: DxfStreamWriter, ctx: DxfWriteContext, entities: Entity[]): void {
    const vars = useSystemVarsStore.getState();
    const { min, max } = drawingBounds(entities);
    w.pair(0, 'SECTION').pair(2, 'HEADER');
    w.pair(9, '$ACADVER').pair(1, ctx.version.version);
    w.pair(9, '$HANDSEED').pair(5, ctx.handles.handSeed());
    w.pair(9, '$INSUNITS').pair(70, 0); // unitless (yap has no unit model)
    w.pair(9, '$PDMODE').pair(70, vars.PDMODE ?? 0);
    w.pair(9, '$PDSIZE').pair(40, vars.PDSIZE ?? 0);
    w.pair(9, '$EXTMIN').pair(10, min[0]).pair(20, min[1]).pair(30, min[2] ?? 0);
    w.pair(9, '$EXTMAX').pair(10, max[0]).pair(20, max[1]).pair(30, max[2] ?? 0);
    w.pair(0, 'ENDSEC');
}
